import { useId, type ReactNode, type SelectHTMLAttributes } from "react";
import { controlClass, type ControlSize } from "./control.ts";
import { describedBy, FieldShell } from "./Field.tsx";
import { ChevronDownIcon } from "./Icons.tsx";

/**
 * A choice from a list, on the native `<select>`.
 *
 * The native control is kept on purpose: on a phone it opens the system wheel or sheet, which
 * people know and which reads well with a screen reader. Only the closed box is drawn, with the
 * same recipe as `Field` and a chevron that says "this opens".
 *
 * The options are `<option>` children, so a list can group them (`<optgroup>`) or disable one.
 */

type NativeSelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "id" | "size" | "children">;

function SelectBox({
  size,
  error,
  className = "",
  children,
  ...rest
}: NativeSelectProps & { size: ControlSize; error?: boolean; id?: string; children: ReactNode }) {
  // `className` sizes the box from outside (`w-full max-w-[220px]`, `flex-1`): it goes on the wrapper,
  // so the chevron stays inside the box. The select always fills the wrapper.
  return (
    <span className={["relative block min-w-0", className].filter(Boolean).join(" ")}>
      <select
        className={controlClass({
          size,
          error,
          className: ["cursor-pointer appearance-none truncate", size === "regular" ? "pr-10 pl-3.5" : "pr-8 pl-3"].join(" "),
        })}
        {...rest}
      >
        {children}
      </select>
      <ChevronDownIcon
        size={size === "regular" ? 18 : 15}
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-ink-3 ${size === "regular" ? "right-3.5" : "right-2.5"}`}
      />
    </span>
  );
}

export type SelectFieldProps = NativeSelectProps & {
  label: string;
  hint?: string;
  error?: string;
  size?: ControlSize;
  children: ReactNode;
};

/** A select with a visible label, a hint and an error, laid out like `Field`. */
export function SelectField({ label, hint, error, size = "regular", children, ...rest }: SelectFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} error={error}>
      <SelectBox
        id={id}
        size={size}
        error={Boolean(error)}
        aria-describedby={describedBy(id, hint, error)}
        aria-invalid={error ? true : undefined}
        {...rest}
      >
        {children}
      </SelectBox>
    </FieldShell>
  );
}

export type SelectProps = NativeSelectProps & {
  /** The accessible name. Not shown: a column header or the line around it names it on screen. */
  label: string;
  size?: ControlSize;
  children: ReactNode;
};

/**
 * A select with no visible label, for a table cell or a toolbar, where the column header or the
 * words beside it already say what it is. Compact unless told otherwise.
 */
export function Select({ label, size = "compact", children, ...rest }: SelectProps) {
  return (
    <SelectBox aria-label={label} size={size} {...rest}>
      {children}
    </SelectBox>
  );
}
